/**
 * Reading and writing theme documents.
 *
 * Everything rides settings endpoints that already exist and are already
 * granted, so theming adds no API surface:
 *
 *   org theme + org presets -> default_table_settings (admin-gated writes)
 *   user theme + presets    -> user_settings (per-user, impersonation-aware)
 *
 * Reads never throw. A theming outage must leave the app looking stock, never
 * blank or broken, so every failure path returns null or an empty list.
 */
import userSettingsService from './userSettings.service';
import type { ThemeDoc } from '../theme/types';

export const ORG_THEME_KEY = 'theme_org_default';
export const USER_THEME_KEY = 'theme_v2';
export const PERSONAL_PRESETS_KEY = 'theme_presets';
export const ORG_PRESET_PREFIX = 'theme_preset_';
/** The pre-v2 key ThemeContext still writes: hue/saturation tint + dark palette. */
export const LEGACY_THEME_KEY = 'theme_preferences';
/** Set once the user has answered the import offer, so it is asked only once. */
export const LEGACY_IMPORT_DISMISSED_KEY = 'theme_legacy_import_dismissed';

export interface ThemePreset {
  slug: string;
  doc: ThemeDoc;
}

/** Settings come back as a JSON string, an object, or wrapped in an envelope. */
function parseDoc(raw: unknown): ThemeDoc | null {
  if (raw === null || raw === undefined) return null;
  let value: unknown = raw;

  if (typeof value === 'object' && value !== null && 'setting_value' in value) {
    value = (value as { setting_value: unknown }).setting_value;
  }
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as ThemeDoc;
  } catch {
    return null;
  }
}

/** Everything written carries the schema version, or it fails validation coming back. */
function stamped(doc: ThemeDoc): ThemeDoc {
  return { ...doc, schemaVersion: 1 };
}

/** 'Warm Paper v2' -> 'warm-paper-v2'. Stable, so re-saving replaces in place. */
export function presetSlug(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')     // strip accents so keys stay ASCII
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

class ThemeService {
  async getOrgTheme(): Promise<ThemeDoc | null> {
    // MUST bail before the request when nobody is signed in.
    //
    // This caused a production login loop. ThemeEngineProvider wraps the router
    // INCLUDING /login, so it mounts on the login page and called this on mount.
    // Unlike getUserSetting - which throws locally when there is no account and
    // never reaches the network - getDefaultSetting issues a real request, so it
    // went out with no token, 401'd, and tripped the global interceptor's
    // redirectToLogin('session_expired'). That navigation remounts the provider
    // on the login page, which calls this again: the login page flashed and
    // reloaded forever.
    //
    // Catching the error here is NOT enough, and that is the lesson: the
    // interceptor acts on the response before this catch ever runs. The request
    // must not be made at all.
    if (!userSettingsService.isAuthenticated()) return null;
    try {
      return parseDoc(await userSettingsService.getDefaultSetting(ORG_THEME_KEY));
    } catch {
      return null;
    }
  }

  /** Publish for everyone. Gated server-side on the edit-org-theme component. */
  async publishOrgTheme(doc: ThemeDoc): Promise<void> {
    await userSettingsService.saveDefaultSetting(ORG_THEME_KEY, stamped(doc));
  }

  async getUserTheme(): Promise<ThemeDoc | null> {
    if (!userSettingsService.isAuthenticated()) return null;
    try {
      return parseDoc(await userSettingsService.getUserSetting(USER_THEME_KEY));
    } catch {
      return null;
    }
  }

  async saveUserTheme(doc: ThemeDoc): Promise<void> {
    await userSettingsService.saveUserSetting(USER_THEME_KEY, stamped(doc));
  }

  /**
   * The user's old hue/saturation tint, if they ever switched one on.
   *
   * Returns null when there is nothing worth offering: no legacy row, the tint
   * disabled, or the offer already answered. The dismissal is stored per-user
   * server-side rather than in localStorage so declining on one machine does not
   * re-ask on the next.
   */
  async getImportableLegacyTheme(): Promise<ThemeDoc | null> {
    if (!userSettingsService.isAuthenticated()) return null;
    try {
      const [raw, dismissed] = await Promise.all([
        userSettingsService.getUserSetting(LEGACY_THEME_KEY),
        userSettingsService.getUserSetting(LEGACY_IMPORT_DISMISSED_KEY),
      ]);
      if (parseDoc(dismissed)) return null;
      const legacy = parseDoc(raw) as LegacyThemePreferences | null;
      return legacyPreferencesToThemeDoc(legacy);
    } catch {
      return null;
    }
  }

  async dismissLegacyImport(): Promise<void> {
    await userSettingsService.saveUserSetting(LEGACY_IMPORT_DISMISSED_KEY, { dismissed: true });
  }

  async listOrgPresets(): Promise<ThemePreset[]> {
    // Same reason as getOrgTheme: a default-settings read without a token 401s
    // and trips the interceptor's redirect.
    if (!userSettingsService.isAuthenticated()) return [];
    try {
      const raw = await userSettingsService.getDefaultSettingsByPrefix(ORG_PRESET_PREFIX);
      return Object.entries(raw ?? {})
        .map(([key, value]) => ({ slug: key.slice(ORG_PRESET_PREFIX.length), doc: parseDoc(value) }))
        // One corrupt preset must not take the whole picker down with it.
        .filter((p): p is ThemePreset => p.doc !== null && p.slug.length > 0);
    } catch {
      return [];
    }
  }

  async saveOrgPreset(name: string, doc: ThemeDoc): Promise<string> {
    const slug = presetSlug(name);
    if (!slug) throw new Error('A preset needs a name with at least one letter or number.');
    await userSettingsService.saveDefaultSetting(
      `${ORG_PRESET_PREFIX}${slug}`,
      stamped({ ...doc, name }),
    );
    return slug;
  }

  async deleteOrgPreset(slug: string): Promise<void> {
    await userSettingsService.deleteDefaultSetting(`${ORG_PRESET_PREFIX}${slug}`);
  }

  /** Personal presets live in ONE blob - a row per preset would be a lot of round trips. */
  async getPersonalPresets(): Promise<ThemePreset[]> {
    if (!userSettingsService.isAuthenticated()) return [];
    try {
      const blob = parseDoc(await userSettingsService.getUserSetting(PERSONAL_PRESETS_KEY)) as
        { presets?: unknown } | null;
      const list = blob?.presets;
      if (!Array.isArray(list)) return [];
      return list
        .map((entry) => {
          const slug = typeof entry?.slug === 'string' ? entry.slug : '';
          const doc = parseDoc(entry?.doc);
          return { slug, doc };
        })
        .filter((p): p is ThemePreset => p.doc !== null && p.slug.length > 0);
    } catch {
      return [];
    }
  }

  async savePersonalPresets(presets: ThemePreset[]): Promise<void> {
    await userSettingsService.saveUserSetting(PERSONAL_PRESETS_KEY, {
      presets: presets.map((p) => ({ slug: p.slug, doc: stamped(p.doc) })),
    });
  }
}

// ---------------------------------------------------------------------------
// Legacy migration
// ---------------------------------------------------------------------------

interface LegacyColor { hue: number; saturation: number }
export interface LegacyThemePreferences {
  themeEnabled?: boolean;
  lightThemeColor?: LegacyColor;
  darkThemeColor?: LegacyColor;
  darkPaletteVariant?: 'grey' | 'blue';
}

/** The old ThemeContext HSL->RGB, reproduced so migrated themes look unchanged. */
function hslToRgb(h: number, s: number, l: number): string {
  const sat = s / 100;
  const lum = l / 100;
  const a = sat * Math.min(lum, 1 - lum);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = lum - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color);
  };
  return `${f(0)} ${f(8)} ${f(4)}`;
}

/**
 * Convert a legacy `theme_preferences` tint into a v2 document.
 *
 * The old system generated six surface values from one hue/saturation pair per
 * mode and wrote them to --theme-bg-* / --theme-border-*. The lightness stops
 * and saturation multipliers below are copied from ThemeContext's
 * generateThemeVariables so a migrated theme is visually identical.
 *
 * Cards are deliberately absent: they were plain bg-white and the old tint never
 * reached them, so inventing a panel value here would change how the app looks
 * rather than preserve it.
 *
 * Returns null when there is nothing worth carrying over.
 */
export function legacyPreferencesToThemeDoc(
  prefs: LegacyThemePreferences | null | undefined,
): ThemeDoc | null {
  if (!prefs || typeof prefs !== 'object') return null;
  if (!prefs.themeEnabled) return null;      // the user was seeing stock colors

  const surfaces = (color: LegacyColor | undefined, isDark: boolean) => {
    const hue = color?.hue ?? 220;
    const sat = (color?.saturation ?? 0) * 0.5;   // the old 0-100 -> max 50% scaling
    return isDark
      ? {
        'sem-surface-canvas': hslToRgb(hue, sat, 7),
        'sem-surface-chrome': hslToRgb(hue, sat, 12),
        'sem-header-bg': hslToRgb(hue, sat, 20),
        'sem-surface-raised': hslToRgb(hue, sat, 12),
        'sem-border': hslToRgb(hue, sat * 0.8, 30),
        'sem-border-strong': hslToRgb(hue, sat * 0.8, 40),
      }
      : {
        'sem-surface-canvas': hslToRgb(hue, sat, 99),
        'sem-surface-chrome': hslToRgb(hue, sat * 1.2, 96),
        'sem-header-bg': hslToRgb(hue, sat * 1.5, 92),
        'sem-surface-raised': hslToRgb(hue, sat * 0.5, 100),
        'sem-border': hslToRgb(hue, sat * 1.2, 88),
        'sem-border-strong': hslToRgb(hue, sat * 1.2, 80),
      };
  };

  return {
    schemaVersion: 1,
    name: 'Imported from your previous theme',
    tokens: {
      light: surfaces(prefs.lightThemeColor, false),
      dark: surfaces(prefs.darkThemeColor, true),
    },
  };
}

export default new ThemeService();
